import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { InstallButton } from "@/components/InstallPWA";
import useKeyboardShortcuts from "@/hooks/useKeyboardShortcuts";
import { resolveAvatar } from "@/lib/api";

const TEACHER_NAV = [
  { to: "/staff/dashboard", icon: "LayoutDashboard", label: "Dashboard" },
  { to: "/staff/students", icon: "Users", label: "Students" },
  { to: "/staff/reviews", icon: "ClipboardCheck", label: "Reviews" },
  { to: "/staff/sessions", icon: "CalendarDays", label: "Sessions" },
  { to: "/staff/attendance", icon: "CheckSquare", label: "Attendance" },
  { to: "/staff/quests", icon: "Target", label: "Quests" },
  { to: "/staff/announcements", icon: "Megaphone", label: "Announce" },
];

const ADMIN_NAV = [
  { to: "/staff/dashboard", icon: "LayoutDashboard", label: "Dashboard" },
  { to: "/staff/batches", icon: "Boxes", label: "Batches" },
  { to: "/staff/students", icon: "Users", label: "Students" },
  { to: "/staff/teachers", icon: "GraduationCap", label: "Teachers" },
  { to: "/staff/program", icon: "Map", label: "Program Builder" },
  { to: "/staff/quests", icon: "Target", label: "Quests" },
  { to: "/staff/badges", icon: "Medal", label: "Badge Builder" },
  { to: "/staff/reviews", icon: "ClipboardCheck", label: "Reviews" },
  { to: "/staff/sessions", icon: "CalendarDays", label: "Sessions" },
  { to: "/staff/attendance", icon: "CheckSquare", label: "Attendance" },
  { to: "/staff/points", icon: "SlidersHorizontal", label: "Point Rules" },
  { to: "/staff/announcements", icon: "Megaphone", label: "Announce" },
  { to: "/staff/reports", icon: "FileBarChart", label: "Reports" },
];

export default function StaffLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "admin" ? ADMIN_NAV : TEACHER_NAV;
  const isAdmin = user?.role === "admin";
  useKeyboardShortcuts(user?.role);

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-16 flex-col gap-1 p-2 md:w-64 md:p-4 gradient-blue sticky top-0 h-screen overflow-y-auto no-scrollbar">
        <div className="mb-6 flex items-center gap-2 px-1 md:px-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl gradient-orange">
            <Icons.Sparkles size={18} className="text-white" />
          </div>
          <div className="hidden font-head text-white md:block leading-none">
            Story<span className="text-csh-orange">Champs</span>
            <div className="text-[10px] font-body font-bold uppercase tracking-widest text-white/60">{isAdmin ? "Admin" : "Coach"}</div>
          </div>
        </div>
        {nav.map((n) => {
          const I = Icons[n.icon];
          return (
            <NavLink key={n.to + n.label} to={n.to} end data-testid={`snav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-2.5 py-2.5 font-semibold text-sm transition-colors ${
                  isActive ? "bg-white text-csh-blue" : "text-white/80 hover:bg-white/10"}`}>
              <I size={20} className="shrink-0" />
              <span className="hidden md:block">{n.label}</span>
            </NavLink>
          );
        })}
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
          className="mt-auto flex items-center gap-3 rounded-xl px-2.5 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/10">
          <Icons.LogOut size={20} className="shrink-0" /> <span className="hidden md:block">Sign Out</span>
        </button>
      </aside>

      <div className="flex-1 overflow-x-hidden">
        <header className="sticky top-0 z-30 flex items-center justify-between bg-background/90 px-5 py-3 backdrop-blur-md border-b border-black/5">
          <h1 className="font-head text-xl text-csh-blue">Welcome, {user?.name}</h1>
          <div className="flex items-center gap-3">
            <InstallButton className="hidden sm:flex" />
            <div className="flex items-center gap-2 rounded-full bg-white p-1 pr-3 shadow-sm border border-black/5">
              <img src={resolveAvatar(user?.avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
              <span className="hidden text-sm font-bold uppercase text-csh-purple sm:block">{user?.role}</span>
            </div>
          </div>
        </header>
        <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="p-5 lg:p-8">
          {children}
        </motion.main>
      </div>
    </div>
  );
}
